import { devLog, devError } from '../utils/devLogger';
import { db } from '../firebase';
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  query,
  where,
  orderBy,
  getDoc,
  getDocs
} from 'firebase/firestore';

class RealTimeProgressService {
  constructor() {
    this.listeners = new Map();
  }

  // Setup progress listener for a specific user
  setupUserProgressListener(userId, callback) {
    const userProgressRef = collection(db, 'users', userId, 'progress');
    const q = query(userProgressRef, orderBy('lastUpdated', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const progressData = {};

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        progressData[doc.id] = {
          id: doc.id,
          ...data,
          lastUpdated: data.lastUpdated?.toDate()?.toISOString() || new Date().toISOString()
        };
      });

      callback(progressData);
    }, (error) => {
      devError('Error listening to user progress:', error);
    });

    this.listeners.set(`user-${userId}`, unsubscribe);
    return unsubscribe;
  }


  // Setup cohort progress listener (for facilitators/admins)
  setupCohortProgressListener(cohortId, callback) {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('cohortId', '==', cohortId));

    // Synchronous snapshot callback — async work is decoupled to avoid blocking the listener queue
    const unsubscribe = onSnapshot(q, (snapshot) => {
      this._fetchProgressForUsers(snapshot.docs, callback);
    }, (error) => {
      devError('Error listening to cohort progress:', error);
    });

    this.listeners.set(`cohort-${cohortId}`, unsubscribe);
    return unsubscribe;
  }

  async _fetchProgressForUsers(userDocs, callback) {
    const cohortProgress = {};
    for (const userDoc of userDocs) {
      const progressRef = collection(db, 'users', userDoc.id, 'progress');
      const progressSnapshot = await getDocs(progressRef);
      const userProgress = {};
      progressSnapshot.docs.forEach(d => {
        userProgress[d.id] = {
          ...d.data(),
          lastUpdated: d.data().lastUpdated?.toDate()?.toISOString()
        };
      });
      cohortProgress[userDoc.id] = { user: userDoc.data(), progress: userProgress };
    }
    callback(cohortProgress);
  }


  // Update user progress in real-time
  async updateUserProgress(userId, moduleId, progressData) {

    try {
      const batch = writeBatch(db);
      
      // Update user's module progress
      const progressRef = doc(db, 'users', userId, 'progress', moduleId);
      batch.update(progressRef, {
        ...progressData,
        lastUpdated: serverTimestamp()
      });

      // Update user's overall stats
      const userRef = doc(db, 'users', userId);
      batch.update(userRef, {
        lastActivity: serverTimestamp(),
        [`stats.modules.${moduleId}.lastUpdated`]: serverTimestamp()
      });

      await batch.commit();
      devLog('Progress updated successfully');
    } catch (error) {
      devError('Error updating progress:', error);
      throw error;
    }
  }


  // Increment lesson completion
  async completeLesson(userId, moduleId, lessonId) {
    const progressData = {
      [`lessons.${lessonId}.completed`]: true,
      [`lessons.${lessonId}.completedAt`]: new Date().toISOString(),
      progress: await this.calculateModuleProgress(userId, moduleId, lessonId)
    };

    return this.updateUserProgress(userId, moduleId, progressData);
  }

  // Calculate module progress percentage
  async calculateModuleProgress(userId, moduleId, completedLessonId = null) {

    try {
      // Get module structure to calculate progress
      const moduleRef = doc(db, 'modules', moduleId);
      const moduleDoc = await getDoc(moduleRef);
      
      if (!moduleDoc.exists()) {
        throw new Error('Module not found');
      }

      const moduleData = moduleDoc.data();
      const totalLessons = moduleData.lessons?.length || 1;
      
      // Get user's lesson completion
      const progressRef = doc(db, 'users', userId, 'progress', moduleId);
      const progressDoc = await getDoc(progressRef);
      
      let completedLessons = 0;
      if (progressDoc.exists()) {
        const progressData = progressDoc.data();
        completedLessons = Object.values(progressData.lessons || {}).filter(
          lesson => lesson.completed
        ).length;
      }

      // Add the newly completed lesson if provided
      if (completedLessonId) {
        completedLessons += 1;
      }

      return Math.round((completedLessons / totalLessons) * 100);
    } catch (error) {
      devError('Error calculating module progress:', error);
      return 0;
    }
  }

  // Setup attendance listener
  setupAttendanceListener(userId, callback) {

    const attendanceRef = collection(db, 'users', userId, 'attendance');
    const q = query(attendanceRef, orderBy('date', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const attendanceData = [];
      
      snapshot.docs.forEach(doc => {
        attendanceData.push({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate()?.toISOString() || new Date().toISOString()
        });
      });

      callback(attendanceData);
    }, (error) => {
      devError('Error listening to attendance:', error);
    });

    this.listeners.set(`attendance-${userId}`, unsubscribe);
    return unsubscribe;
  }


  // Clean up a specific listener
  removeListener(key) {
    if (this.listeners.has(key)) {
      const unsubscribe = this.listeners.get(key);
      unsubscribe();
      this.listeners.delete(key);
    }
  }

  // Clean up all listeners
  cleanup() {
    // Clean up Firebase listeners
    this.listeners.forEach((unsubscribe, key) => {
      unsubscribe();
    });
    this.listeners.clear();

    devLog('Real-time Progress Service: All listeners cleaned up');
  }

  // Get real-time stats for dashboard — two independent listeners combined into one callback
  setupDashboardStatsListener(userId, callback) {
    const latest = { progress: null, attendance: null };

    const progressUnsubscribe = this.setupUserProgressListener(userId, (progress) => {
      latest.progress = progress;
      if (latest.attendance !== null) {
        callback(this.calculateDashboardStats(latest.progress, latest.attendance));
      }
    });

    const attendanceUnsubscribe = this.setupAttendanceListener(userId, (attendance) => {
      latest.attendance = attendance;
      if (latest.progress !== null) {
        callback(this.calculateDashboardStats(latest.progress, latest.attendance));
      }
    });

    // Return a cleanup function that tears down both listeners
    return () => {
      progressUnsubscribe();
      attendanceUnsubscribe();
    };
  }


  // Calculate dashboard statistics
  calculateDashboardStats(progress, attendance) {
    const stats = {
      overallProgress: 0,
      modulesCompleted: 0,
      totalModules: 0,
      attendanceRate: 0,
      weeklyStudyTime: 0,
      achievements: 0,
      lastActivity: null
    };

    if (progress.moduleProgress) {
      const moduleProgresses = Object.values(progress.moduleProgress);
      stats.totalModules = moduleProgresses.length;
      stats.modulesCompleted = moduleProgresses.filter(m => m.progress >= 100).length;
      
      const totalProgress = moduleProgresses.reduce((sum, m) => sum + (m.progress || 0), 0);
      stats.overallProgress = stats.totalModules > 0 ? Math.round(totalProgress / stats.totalModules) : 0;
      
      // Count achievements
      stats.achievements = moduleProgresses.reduce((sum, m) => 
        sum + (m.achievements?.length || 0), 0
      );

      // Get last activity
      const lastUpdates = moduleProgresses
        .map(m => m.lastUpdated)
        .filter(Boolean)
        .sort()
        .reverse();
      stats.lastActivity = lastUpdates[0] || null;
    }

    if (attendance && attendance.length > 0) {
      const presentCount = attendance.filter(a => a.status === 'present').length;
      stats.attendanceRate = Math.round((presentCount / attendance.length) * 100);
      
      // Calculate weekly study time
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      stats.weeklyStudyTime = attendance
        .filter(a => new Date(a.date) >= weekAgo && a.status === 'present')
        .reduce((sum, a) => sum + (a.duration || 0), 0);
    }

    return stats;
  }

  // ===== COMPLETED MODULES SUBCOLLECTION IMPLEMENTATION =====

  // Mark a module as completed
  async completeModule(userId, moduleId, moduleData = {}) {

    try {
      const batch = writeBatch(db);
      
      // Add to user's completedModules subcollection
      const completedModuleRef = doc(db, 'users', userId, 'completedModules', moduleId);
      batch.set(completedModuleRef, {
        moduleId,
        moduleName: moduleData.moduleName || '',
        completedAt: serverTimestamp(),
        finalScore: moduleData.finalScore || null,
        timeSpent: moduleData.timeSpent || 0,
        certificateIssued: moduleData.certificateIssued || false,
        achievements: moduleData.achievements || [],
        progress: 100,
        completionData: {
          lessonsCompleted: moduleData.lessonsCompleted || 0,
          assignmentsCompleted: moduleData.assignmentsCompleted || 0,
          quizzesCompleted: moduleData.quizzesCompleted || 0,
          ...moduleData.completionData
        }
      });

      // Update user's progress subcollection to reflect completion
      const progressRef = doc(db, 'users', userId, 'progress', moduleId);
      batch.update(progressRef, {
        progress: 100,
        completed: true,
        completedAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });

      // Update user's main document stats
      const userRef = doc(db, 'users', userId);
      batch.update(userRef, {
        'stats.modulesCompleted': (await this.getCompletedModulesCount(userId)) + 1,
        lastActivity: serverTimestamp(),
        [`stats.modules.${moduleId}.completed`]: true,
        [`stats.modules.${moduleId}.completedAt`]: serverTimestamp()
      });

      await batch.commit();
      devLog(`Module ${moduleId} marked as completed for user ${userId}`);
      
      return true;
    } catch (error) {
      devError('Error completing module:', error);
      throw error;
    }
  }


  // Get user's completed modules
  async getCompletedModules(userId) {

    try {
      const completedModulesRef = collection(db, 'users', userId, 'completedModules');
      const q = query(completedModulesRef, orderBy('completedAt', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const completedModules = [];
      
      querySnapshot.forEach((doc) => {
        completedModules.push({
          id: doc.id,
          ...doc.data(),
          completedAt: doc.data().completedAt?.toDate()?.toISOString() || null
        });
      });
      
      return completedModules;
    } catch (error) {
      devError('Error getting completed modules:', error);
      return [];
    }
  }

  // Get count of completed modules — uses server-side aggregation, zero document transfer
  async getCompletedModulesCount(userId) {
    try {
      const { getCountFromServer } = await import('firebase/firestore');
      const q = collection(db, 'users', userId, 'progress');
      const result = await getCountFromServer(q);
      return result.data().count;
    } catch (error) {
      devError('Error getting completed modules count:', error);
      return 0;
    }
  }

  // Setup real-time listener for completed modules
  setupCompletedModulesListener(userId, callback) {

    const completedModulesRef = collection(db, 'users', userId, 'completedModules');
    const q = query(completedModulesRef, orderBy('completedAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const completedModules = [];
      
      snapshot.docs.forEach(doc => {
        completedModules.push({
          id: doc.id,
          ...doc.data(),
          completedAt: doc.data().completedAt?.toDate()?.toISOString() || null
        });
      });

      callback(completedModules);
    }, (error) => {
      devError('Error listening to completed modules:', error);
    });

    this.listeners.set(`completedModules-${userId}`, unsubscribe);
    return unsubscribe;
  }


  // Check if a specific module is completed
  async isModuleCompleted(userId, moduleId) {

    try {
      const completedModuleRef = doc(db, 'users', userId, 'completedModules', moduleId);
      const docSnapshot = await getDoc(completedModuleRef);
      return docSnapshot.exists();
    } catch (error) {
      devError('Error checking if module is completed:', error);
      return false;
    }
  }

  // Get completion certificate data for a module
  async getModuleCertificate(userId, moduleId) {

    try {
      const completedModuleRef = doc(db, 'users', userId, 'completedModules', moduleId);
      const docSnapshot = await getDoc(completedModuleRef);
      
      if (!docSnapshot.exists()) {
        return null;
      }

      const moduleData = docSnapshot.data();
      if (moduleData.certificateIssued) {
        return {
          moduleId,
          userId,
          moduleName: moduleData.moduleName,
          completedAt: moduleData.completedAt?.toDate()?.toISOString(),
          finalScore: moduleData.finalScore,
          certificateUrl: moduleData.certificateUrl || null
        };
      }
      
      return null;
    } catch (error) {
      devError('Error getting module certificate:', error);
      return null;
    }
  }

  // Get completion statistics
  async getCompletionStatistics(userId) {

    try {
      const [completedModules, progressModules] = await Promise.all([
        this.getCompletedModules(userId),
        this.getUserProgress(userId)
      ]);
      
      const progressArray = Object.values(progressModules || {});
      
      return {
        totalModulesStarted: progressArray.length,
        totalModulesCompleted: completedModules.length,
        completionRate: progressArray.length > 0 ? (completedModules.length / progressArray.length) * 100 : 0,
        averageScore: completedModules.length > 0 ? 
          completedModules.reduce((sum, m) => sum + (m.finalScore || 0), 0) / completedModules.length : 0,
        totalTimeSpent: completedModules.reduce((sum, m) => sum + (m.timeSpent || 0), 0),
        certificatesEarned: completedModules.filter(m => m.certificateIssued).length,
        achievements: completedModules.reduce((sum, m) => sum + (m.achievements?.length || 0), 0)
      };
    } catch (error) {
      devError('Error getting completion statistics:', error);
      return null;
    }
  }

  // Helper method to get user progress (used internally)
  async getUserProgress(userId) {
    try {
      const progressRef = collection(db, 'users', userId, 'progress');
      const querySnapshot = await getDocs(progressRef);
      
      const progressData = {};
      querySnapshot.forEach((doc) => {
        progressData[doc.id] = {
          ...doc.data(),
          lastUpdated: doc.data().lastUpdated?.toDate()?.toISOString()
        };
      });
      
      return progressData;
    } catch (error) {
      devError('Error getting user progress:', error);
      return {};
    }
  }
}

const realTimeProgressService = new RealTimeProgressService();
export default realTimeProgressService;